import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, Download, FileText, Upload, CheckCircle, FileUp} from 'lucide-react'
import { lumi } from '../lib/lumi'
import jsPDF from 'jspdf'

export default function ComprovantePagamento() {
  const navigate = useNavigate()
  
  // Dados da Empresa
  const [nomeEmpresa, setNomeEmpresa] = useState('')
  const [cpfCnpjEmpresa, setCpfCnpjEmpresa] = useState('')
  const [telefoneEmpresa, setTelefoneEmpresa] = useState('')
  const [emailEmpresa, setEmailEmpresa] = useState('')
  const [enderecoEmpresa, setEnderecoEmpresa] = useState('')
  const [cidadeEmpresa, setCidadeEmpresa] = useState('')
  const [estadoEmpresa, setEstadoEmpresa] = useState('')
  const [cepEmpresa, setCepEmpresa] = useState('')
  
  // Dados do Cliente
  const [nomeCliente, setNomeCliente] = useState('')
  const [cpfCnpjCliente, setCpfCnpjCliente] = useState('')
  const [contatoCliente, setContatoCliente] = useState('')
  const [enderecoCliente, setEnderecoCliente] = useState('')
  
  // Informações do Pagamento
  const [servicoProduto, setServicoProduto] = useState('')
  const [descricaoServico, setDescricaoServico] = useState('')
  const [numeroReferencia, setNumeroReferencia] = useState('')
  const [valorPago, setValorPago] = useState('')
  const [formaPagamento, setFormaPagamento] = useState('pix')
  const [dataPagamento, setDataPagamento] = useState(new Date().toISOString().split('T')[0])
  
  // Comprovação de Pagamento (Upload)
  const [comprovanteFile, setComprovanteFile] = useState<File | null>(null)
  const [comprovanteUrl, setComprovanteUrl] = useState('')
  const [uploadLoading, setUploadLoading] = useState(false)
  
  // Anotações
  const [anotacoesInternas, setAnotacoesInternas] = useState('')
  const [observacoesPDF, setObservacoesPDF] = useState('')
  
  const [loading, setLoading] = useState(false)
  const [pdfGerado, setPdfGerado] = useState(false)

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // Validar tipo de arquivo (apenas imagens)
    const tiposPermitidos = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg']
    if (!tiposPermitidos.includes(file.type)) {
      alert('Tipo de arquivo não suportado. Use: JPG, PNG ou WEBP.')
      return
    }
    
    // Validar tamanho (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Arquivo muito grande. Tamanho máximo: 10MB')
      return
    }
    
    setUploadLoading(true)
    
    try {
      const result = await lumi.tools.file.upload([file])
      if (result && result.length > 0) {
        const url = typeof result[0] === 'string' ? result[0] : result[0].fileUrl
        setComprovanteUrl(url || '')
        setComprovanteFile(file)
        alert('Comprovante enviado com sucesso!')
      }
    } catch (error) {
      console.error('Erro ao enviar comprovante:', error)
      alert('Erro ao enviar comprovante. Tente novamente.')
    } finally {
      setUploadLoading(false)
    }
  }

  const gerarComprovante = async () => {
    if (!nomeEmpresa || !cpfCnpjEmpresa || !telefoneEmpresa || !emailEmpresa ||
        !nomeCliente || !servicoProduto || !valorPago || !formaPagamento) {
      alert('Preencha todos os campos obrigatórios (*)')
      return
    }

    setLoading(true)

    try {
      const user = lumi.auth.user
      if (!user) {
        alert('Usuário não autenticado')
        return
      }

      const numeroComprovante = `COMP-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`
      
      // Salvar no MongoDB
      const userId = user.userId
      await lumi.entities.comprovantes?.create({
        numeroComprovante,
        nomeEmpresa,
        cpfCnpjEmpresa,
        telefoneEmpresa,
        emailEmpresa,
        nomeCliente,
        cpfCnpjCliente: cpfCnpjCliente || '',
        contatoCliente,
        servicoProduto,
        descricaoServico: descricaoServico || '',
        numeroReferencia: numeroReferencia || '',
        valorPago: parseFloat(valorPago),
        formaPagamento,
        dataPagamento,
        comprovanteUrl: comprovanteUrl || '',
        anotacoesInternas: anotacoesInternas || '',
        observacoesPDF: observacoesPDF || '',
        creator: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }).catch(() => {
        console.log('Coleção comprovantes não existe ainda - PDF será gerado normalmente')
      })

      // Gerar PDF Profissional
      const doc = new jsPDF()
      const logoUrl = 'https://static.lumi.new/94/94387bd69277769ec0567b4f6dbc1e0e.webp'
      
      // Cabeçalho
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('COMPROVANTE DE PAGAMENTO', 105, 18, { align: 'center' })
      
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(100, 100, 100)
      doc.text(`Comprovante Nº ${numeroComprovante}`, 105, 25, { align: 'center' })
      doc.text(`Emitido em ${new Date().toLocaleDateString('pt-BR')}`, 105, 29, { align: 'center' })
      
      // Linha separadora
      doc.setDrawColor(139, 0, 0)
      doc.setLineWidth(0.8)
      doc.line(20, 33, 190, 33)
      
      let yPos = 40
      
      // EMITENTE (Empresa que recebeu)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('EMITENTE (RECEBEDOR)', 20, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(nomeEmpresa, 20, yPos)
      
      yPos += 4
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(80, 80, 80)
      doc.text(`${cpfCnpjEmpresa.length <= 14 ? 'CPF' : 'CNPJ'}: ${cpfCnpjEmpresa} | Tel: ${telefoneEmpresa} | ${emailEmpresa}`, 20, yPos)
      
      if (enderecoEmpresa) {
        yPos += 3
        const enderecoCompleto = `${enderecoEmpresa}${cidadeEmpresa ? ', ' + cidadeEmpresa : ''}${estadoEmpresa ? ' - ' + estadoEmpresa : ''}${cepEmpresa ? ', ' + cepEmpresa : ''}`
        doc.text(enderecoCompleto, 20, yPos)
      }
      
      // PAGADOR (Cliente)
      yPos += 8
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('PAGADOR', 20, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(nomeCliente, 20, yPos)
      
      yPos += 4
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(80, 80, 80)
      const clienteInfo = cpfCnpjCliente 
        ? `${cpfCnpjCliente.length <= 14 ? 'CPF' : 'CNPJ'}: ${cpfCnpjCliente} | Contato: ${contatoCliente}`
        : `Contato: ${contatoCliente}`
      doc.text(clienteInfo, 20, yPos)
      
      if (enderecoCliente) {
        yPos += 3
        doc.text(enderecoCliente, 20, yPos)
      }
      
      // DETALHES DO PAGAMENTO
      yPos += 8
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text('DETALHES DO PAGAMENTO', 20, yPos)
      
      yPos += 5
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0, 0, 0)
      doc.text(servicoProduto, 20, yPos)
      
      if (descricaoServico) {
        yPos += 4
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(60, 60, 60)
        const descLines = doc.splitTextToSize(descricaoServico, 170)
        doc.text(descLines, 20, yPos)
        yPos += descLines.length * 3
      }
      
      yPos += 4
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      doc.text(`• Data do Pagamento: ${new Date(dataPagamento).toLocaleDateString('pt-BR')}`, 22, yPos)
      yPos += 4
      doc.text(`• Forma de Pagamento: ${formaPagamento.toUpperCase()}`, 22, yPos)
      if (numeroReferencia) {
        yPos += 4
        doc.text(`• Nº Referência/Transação: ${numeroReferencia}`, 22, yPos)
      }
      
      // Box de destaque do valor
      yPos += 8
      doc.setFillColor(240, 240, 240)
      doc.roundedRect(20, yPos - 3, 170, 20, 3, 3, 'F')
      
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(139, 0, 0)
      doc.text(`VALOR PAGO: R$ ${parseFloat(valorPago).toFixed(2).replace('.', ',')}`, 105, yPos + 7, { align: 'center' })
      
      yPos += 22
      
      // OBSERVAÇÕES (se houver)
      if (observacoesPDF) {
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(139, 0, 0)
        doc.text('OBSERVAÇÕES', 20, yPos)
        yPos += 4
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(60, 60, 60)
        const obsLines = doc.splitTextToSize(observacoesPDF, 170)
        doc.text(obsLines, 20, yPos)
        yPos += obsLines.length * 3 + 4
      }
      
      // COMPROVANTE ANEXADO (se houver)
      if (comprovanteUrl) {
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(100, 100, 100)
        doc.text('✓ Comprovante bancário anexado (arquivo digital)', 20, yPos)
        yPos += 4
        
        // Tentar adicionar imagem do comprovante no PDF
        try {
          doc.addImage(comprovanteUrl, 'JPEG', 20, yPos, 80, 60)
          yPos += 65
        } catch (e) {
          console.log('Não foi possível incluir imagem no PDF')
        }
      }
      
      // Assinaturas
      yPos = Math.max(yPos, 220)
      doc.setFontSize(8)
      doc.setTextColor(0, 0, 0)
      doc.setLineWidth(0.3)
      doc.setDrawColor(200, 200, 200)
      doc.line(25, yPos, 85, yPos)
      doc.text('Assinatura do Pagador', 55, yPos + 4, { align: 'center' })
      
      doc.line(125, yPos, 185, yPos)
      doc.text('Assinatura do Recebedor', 155, yPos + 4, { align: 'center' })
      
      yPos += 10
      doc.setFontSize(7)
      doc.setTextColor(150, 150, 150)
      doc.text(`${nomeCliente}`, 55, yPos, { align: 'center' })
      doc.text(`${nomeEmpresa}`, 155, yPos, { align: 'center' })
      
      // Rodapé preto com logo pequena
      doc.setFillColor(0, 0, 0)
      doc.rect(0, 275, 210, 22, 'F')
      
      // Logo pequena no rodapé
      try {
        doc.addImage(logoUrl, 'PNG', 15, 278, 12, 12)
      } catch (e) {
        console.log('Logo no rodapé não carregada')
      }
      
      doc.setFontSize(8)
      doc.setTextColor(255, 255, 255)
      doc.text(nomeEmpresa, 105, 282, { align: 'center' })
      doc.setFontSize(7)
      doc.text(`${telefoneEmpresa} | ${emailEmpresa}`, 105, 287, { align: 'center' })
      
      // Download
      doc.save(`Comprovante_${numeroComprovante}_${nomeCliente.replace(/\s+/g, '_')}.pdf`)
      
      setPdfGerado(true)
      setTimeout(() => {
        navigate('/dashboard')
      }, 2000)
    } catch (error) {
      console.error('Erro ao gerar comprovante:', error)
      alert('Erro ao gerar comprovante. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#8B0000]">Comprovante de Pagamento</h1>
              <p className="text-xs text-gray-600">Gere recibo profissional em PDF</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            
            {/* SEÇÃO 1: DADOS DA SUA EMPRESA */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">1. Dados da Sua Empresa (Recebedor)</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nome da Empresa *</label>
                  <input
                    type="text"
                    value={nomeEmpresa}
                    onChange={(e) => setNomeEmpresa(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Digite o nome da sua empresa"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">CPF ou CNPJ *</label>
                  <input
                    type="text"
                    value={cpfCnpjEmpresa}
                    onChange={(e) => setCpfCnpjEmpresa(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone/WhatsApp *</label>
                    <input
                      type="tel"
                      value={telefoneEmpresa}
                      onChange={(e) => setTelefoneEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">E-mail *</label>
                    <input
                      type="email"
                      value={emailEmpresa}
                      onChange={(e) => setEmailEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="contato@empresa.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Endereço Completo</label>
                  <input
                    type="text"
                    value={enderecoEmpresa}
                    onChange={(e) => setEnderecoEmpresa(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Rua, número, bairro"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Cidade</label>
                    <input
                      type="text"
                      value={cidadeEmpresa}
                      onChange={(e) => setCidadeEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Cidade"
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Estado</label>
                    <input
                      type="text"
                      value={estadoEmpresa}
                      onChange={(e) => setEstadoEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">CEP</label>
                    <input
                      type="text"
                      value={cepEmpresa}
                      onChange={(e) => setCepEmpresa(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="00000-000"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SEÇÃO 2: DADOS DO CLIENTE (PAGADOR) */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">2. Dados do Cliente (Pagador)</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Completo ou Razão Social *</label>
                  <input
                    type="text"
                    value={nomeCliente}
                    onChange={(e) => setNomeCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Digite o nome do cliente"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">CPF ou CNPJ do Cliente</label>
                  <input
                    type="text"
                    value={cpfCnpjCliente}
                    onChange={(e) => setCpfCnpjCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone/WhatsApp *</label>
                  <input
                    type="tel"
                    value={contatoCliente}
                    onChange={(e) => setContatoCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Endereço do Cliente</label>
                  <input
                    type="text"
                    value={enderecoCliente}
                    onChange={(e) => setEnderecoCliente(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Rua, número, bairro, cidade - UF"
                  />
                </div>
              </div>
            </div>

            {/* SEÇÃO 3: INFORMAÇÕES DO PAGAMENTO */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">3. Informações do Pagamento</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Serviço/Produto *</label>
                  <input
                    type="text"
                    value={servicoProduto}
                    onChange={(e) => setServicoProduto(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: Desenvolvimento de Website"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Descrição do Serviço/Produto</label>
                  <textarea
                    value={descricaoServico}
                    onChange={(e) => setDescricaoServico(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Detalhe o que foi pago..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nº Referência/Transação</label>
                  <input
                    type="text"
                    value={numeroReferencia}
                    onChange={(e) => setNumeroReferencia(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: PIX123456 ou ID da transação"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Valor Pago *</label>
                  <input
                    type="number"
                    value={valorPago}
                    onChange={(e) => setValorPago(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: 1500.00"
                    step="0.01"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Forma de Pagamento *</label>
                    <select
                      value={formaPagamento}
                      onChange={(e) => setFormaPagamento(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="pix">PIX</option>
                      <option value="dinheiro">Dinheiro</option>
                      <option value="cartao_credito">Cartão de Crédito</option>
                      <option value="cartao_debito">Cartão de Débito</option>
                      <option value="transferencia">Transferência Bancária</option>
                      <option value="boleto">Boleto Bancário</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Data do Pagamento *</label>
                    <input
                      type="date"
                      value={dataPagamento}
                      onChange={(e) => setDataPagamento(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* SEÇÃO 4: COMPROVANTE DE PAGAMENTO (UPLOAD) */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">4. Comprovante Bancário (Opcional)</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-xs text-blue-800 font-semibold mb-1">💡 Anexe o print do pagamento</p>
                <p className="text-xs text-blue-700">
                  Print do PIX, screenshot da transferência, foto do recibo bancário, etc.
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Enviar Comprovante (Imagem)
                  </label>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#8B0000] transition-colors">
                    <input
                      type="file"
                      id="fileUpload"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="fileUpload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      {uploadLoading ? (
                        <div className="text-gray-600">
                          <Upload className="animate-pulse mx-auto mb-2" size={32} />
                          <p className="text-sm">Enviando arquivo...</p>
                        </div>
                      ) : comprovanteUrl ? (
                        <div className="text-green-600">
                          <CheckCircle size={32} className="mx-auto mb-2" />
                          <p className="text-sm font-semibold">Comprovante enviado com sucesso!</p>
                          <p className="text-xs text-gray-600 mt-1">
                            {comprovanteFile?.name || 'comprovante.jpg'}
                          </p>
                          <p className="text-xs text-blue-600 mt-2 hover:underline">Clique para trocar arquivo</p>
                        </div>
                      ) : (
                        <div className="text-gray-600">
                          <FileUp size={32} className="mx-auto mb-2" />
                          <p className="text-sm font-semibold">Clique para enviar comprovante</p>
                          <p className="text-xs text-gray-500 mt-1">JPG, PNG ou WEBP (max 10MB)</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {comprovanteUrl && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-green-800">
                      <strong>✓ Arquivo anexado:</strong> {comprovanteFile?.name || 'comprovante.jpg'}
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Este comprovante será incluído no PDF gerado
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* SEÇÃO 5: ANOTAÇÕES */}
            <div className="pb-4">
              <h2 className="text-lg font-bold text-[#8B0000] mb-4">5. Anotações e Observações</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Anotações Internas (não aparece no PDF)
                  </label>
                  <textarea
                    value={anotacoesInternas}
                    onChange={(e) => setAnotacoesInternas(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-yellow-50"
                    placeholder="Anotações pessoais, lembretes, status do cliente... (só você vê)"
                  />
                  <p className="text-xs text-gray-500 mt-1">🔒 Privado - salvo apenas no sistema</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Observações no Comprovante (aparece no PDF)
                  </label>
                  <textarea
                    value={observacoesPDF}
                    onChange={(e) => setObservacoesPDF(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Ex: Pagamento referente ao projeto X, quitação total do serviço..."
                  />
                  <p className="text-xs text-gray-500 mt-1">📄 Público - aparece no PDF do comprovante</p>
                </div>
              </div>
            </div>

            <button
              onClick={gerarComprovante}
              disabled={loading}
              className="w-full bg-[#8B0000] text-white py-3 rounded-lg font-semibold hover:bg-[#6B0000] transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2 text-sm mt-6"
            >
              {loading ? (
                'Gerando Comprovante...'
              ) : (
                <>
                  <FileText size={18} />
                  Gerar Comprovante Profissional em PDF
                </>
              )}
            </button>

            {pdfGerado && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <Download size={32} className="mx-auto text-green-600 mb-2" />
                <p className="text-sm font-semibold text-green-800">Comprovante gerado e baixado com sucesso!</p>
                <p className="text-xs text-green-600 mt-1">Redirecionando para o dashboard...</p>
              </div>
            )}
          </div>
        </div>

        {/* Informações adicionais */}
        <div className="bg-gray-100 rounded-lg p-3 mt-4">
          <p className="text-xs text-gray-700 font-semibold mb-2">💡 Ideal para:</p>
          <ul className="text-xs text-gray-600 space-y-1 ml-4">
            <li>• Comprovar recebimento de pagamentos</li>
            <li>• Serviços presenciais e freelancers</li>
            <li>• Vendas informais organizadas</li>
            <li>• Registros internos e histórico financeiro</li>
          </ul>
        </div>
      </main>
    </div>
  )
}
